import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Globe, ArrowLeft, ChevronRight } from 'lucide-react';
import SEO from './SEO';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { MAKEUP_ARTISTS, type MakeupArtist } from './MakeupHair';
import SocialLinks from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1487412912498-0447578fcca8?w=1200&h=800&fit=crop';

// Same slug derivation the category page uses (slugify of name), exported here
// so card links and the detail page always agree — including admin-added artists.
export function artistSlug(name: string) {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export default function MakeupArtistDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('makeup_hair', MAKEUP_ARTISTS, (a, i) =>
    slugify(a.name || String(i))
  );

  const entry: MakeupArtist | undefined = mc.items.find(
    (a: any) => artistSlug(a.name) === slug
  );

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Beauty Services Artist Not Found | Dallas Desi Scoop"
          description="This beauty services listing could not be found."
          canonical="/BeautyServices"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Artist not found.</p>
        <Link
          to="/BeautyServices"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Beauty Services
        </Link>
      </div>
    );
  }

  const typeList = Array.isArray(entry.type) ? entry.type : entry.type ? [entry.type] : [];
  const typeLabel = typeList.length ? typeList.join(', ') : 'Makeup & Hair';
  const city = entry.city || 'DFW';
  const title = `${entry.name} in ${city}, TX | Beauty Services | Dallas Desi Scoop`;
  const description = `${entry.name} offers ${typeLabel.toLowerCase()} services in ${city}, TX. Find contact details and booking info for bridal makeup, hair styling, and henna across DFW.`;
  const canonicalPath = `/BeautyServices/${slug}`;

  const jsonLd = [
    {
      '@type': 'LocalBusiness',
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description: entry.description || `${typeLabel} services in Dallas-Fort Worth`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: city,
        addressRegion: 'TX',
        addressCountry: 'US',
      },
      telephone: entry.phone && entry.phone !== 'Coming soon' ? entry.phone : undefined,
      url: entry.website || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Beauty Services', item: `${BASE_URL}/BeautyServices` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={description}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/BeautyServices"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Beauty Services
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {entry.city && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                {entry.city}
              </span>
            )}
            <span className="text-white/40">·</span>
            <span className="text-white/75 text-[13px]">{typeLabel}</span>
          </motion.div>
        </div>
      </section>

      <CategoryNav />

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {entry.description
              ? entry.description
              : `${entry.name} offers ${typeLabel.toLowerCase()} services for the South Asian community across Dallas–Fort Worth`}
            {entry.city ? `, based in ${entry.city}, TX` : ''}.
            {' '}Serving weddings, engagements, festivals, and special occasions for families across DFW.
            {entry.address && entry.address !== 'Address coming soon' ? ` Located at ${entry.address}.` : ''}
          </p>
          <h2 className="text-[18px] font-bold text-gray-900 mt-8 mb-3">Bridal & Beauty Services</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            Looking for Indian bridal makeup, South Asian hair styling, saree draping, or henna artists in {city}? {entry.name} offers services for celebrations of every size, from intimate gatherings to full wedding parties.
          </p>
          <div className="mt-6 max-w-xs">
            <SocialLinks item={{ ...entry, website: undefined }} variant="bar" />
          </div>
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact & Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            <div className="flex items-start gap-2">
              <span>{entry.address && entry.address !== 'Address coming soon' ? entry.address : '[Coming soon]'}</span>
            </div>
            {entry.phone && entry.phone !== 'Coming soon' && (
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a href={`tel:${entry.phone.replace(/[^\d+]/g, '')}`} className="hover:underline">
                  {entry.phone}
                </a>
              </div>
            )}
            {entry.website && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a
                  href={entry.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline break-all"
                >
                  Visit Website
                </a>
              </div>
            )}
          </div>
          {entry.contactUrl && entry.contactUrl !== '#' && (
            <a
              href={entry.contactUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 block text-center bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              Contact / Instagram
            </a>
          )}
          <Link
            to="/BeautyServices"
            className="mt-3 block text-center text-[#082f6d] text-[12px] font-semibold hover:underline"
          >
            Browse all beauty services
          </Link>
        </div>
      </div>
    </div>
  );
}
